import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Relative time: "2m ago", "3h ago", "4d ago". */
export function timeAgo(input: string | Date): string {
  const d = typeof input === "string" ? new Date(input) : input;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

/** Euro cents → "€12.40". */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `€${(cents / 100).toFixed(2)}`;
}

export function toNumber(value: unknown, fallback = 0): number {
  if (value == null) return fallback;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatNumber(value: unknown, options?: Intl.NumberFormatOptions): string {
  const n = toNumber(value, Number.NaN);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, options);
}

export function formatCurrency(value: unknown): string {
  const n = toNumber(value, Number.NaN);
  if (!Number.isFinite(n)) return "—";
  return `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function formatPercent(value: unknown, decimals = 1): string {
  const n = toNumber(value, Number.NaN);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(decimals)}%`;
}

// WEB-06: calendar-day due-window helpers live in the API package (which has a
// test runner) and are re-exported here so existing `@/lib/utils` imports keep
// working. The regression test lives next to the source in packages/api.
export { daysUntil, isDueWithin } from "@bw-dwh/api/lib/dates";
